import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";

// L'éditeur d'un workflow est une page à part depuis le 01/10 : ProjectWorkflowEditor.js (décision « page entière »).

export default function ProjectWorkflows() {
  const { confirm } = useConfirm();
  const [workflows, setWorkflows] = useState([]);
  const navigate = useNavigate();
  const load = () => api.get("/admin/project-workflows").then((r) => setWorkflows(r.data));
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const g = {};
    workflows.forEach((w) => { (g[w.type] = g[w.type] || { type: w.type, label: w.type_label, items: [] }).items.push(w); });
    return Object.values(g);
  }, [workflows]);

  const openEdit = (wf) => navigate(`/admin/automations-workflows-prestation/${wf.id}`);
  const openCreate = (grp) => navigate(`/admin/automations-workflows-prestation/nouveau?type=${encodeURIComponent(grp.type)}`);

  const duplicate = async (wf) => {
    try { await api.post(`/admin/project-workflows/${wf.id}/duplicate`); toast.success("Workflow dupliqué"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Duplication impossible"); }
  };

  const del = async (wf) => {
    if (!(await confirm({ title: `Supprimer le workflow « ${wf.name} » ?`, confirmLabel: "Supprimer", tone: "danger" }))) return;
    try { await api.delete(`/admin/project-workflows/${wf.id}`); toast.success("Workflow supprimé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Suppression impossible"); }
  };

  return (
    <div>
      <PageTitle overline="Automatisations" title="Workflows des prestations" />
      <p className="text-sm text-marine/80 mb-5">Créez plusieurs workflows par type de module, renommez-les et corrigez leurs étapes à tout moment. À la création d'un projet, vous choisirez le workflow à appliquer. Marquez « Étape obligatoire » (dans le panneau de l'étape) celles qui bloquent la suite du projet.</p>
      <div className="space-y-6" data-testid="project-workflows-list">
        {groups.map((grp) => (
          <div key={grp.type} data-testid={`pwf-group-${grp.type}`}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display text-lg text-marine">{grp.label}</h2>
              <button onClick={() => openCreate(grp)} data-testid={`pwf-new-${grp.type}`} className="text-xs text-bordeaux hover:underline flex items-center gap-1"><Icons.Plus size={16} /> Nouveau workflow</button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {grp.items.map((wf) => (
                <div key={wf.id} data-testid={`pwf-${wf.id}`} className="bg-sable rounded-sm border border-marine/10 p-4 hover:border-marine/40 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-marine flex items-center gap-1.5">{wf.name}{wf.is_default && <span className="text-[9px] uppercase tracking-wider bg-marine-50 text-marine border border-marine/30 px-1.5 py-0.5 rounded-sm">défaut</span>}</div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(wf)} data-testid={`pwf-edit-${wf.id}`} className="text-marine hover:text-bordeaux" title="Modifier"><Icons.Pencil size={16} /></button>
                      <button onClick={() => duplicate(wf)} data-testid={`pwf-dup-${wf.id}`} className="text-marine hover:text-bordeaux" title="Dupliquer"><Icons.Copy size={16} /></button>
                      <button onClick={() => del(wf)} data-testid={`pwf-del-${wf.id}`} className="text-rouge/70 hover:text-rouge" title="Supprimer"><Icons.Trash2 size={16} /></button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {(wf.phases || []).map((ph, pi) => (
                      <div key={pi}>
                        {wf.phases.length > 1 && <div className="text-xs uppercase tracking-wider text-bordeaux mb-0.5">{ph.name}</div>}
                        <div className="flex flex-wrap gap-1.5">
                          {ph.etapes.map((p, i) => (
                            <span key={i} className={`text-[9px] px-2 py-0.5 rounded-sm border ${p.blocking ? "bg-marine-50 text-marine border-marine/30" : "bg-sable text-gris-texte border-marine/30"}`}>{p.name}{p.blocking ? " ✔" : ""}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
